import { hydrate } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { App } from "./App";
import { isOnBlogSubdomain } from "./lib/blog-host";
import { releaseBuildClock } from "./lib/build-clock";
import { createQueryClient } from "./lib/query-client";
import { buildRoutes, createAppRouter, idlePrefetchRoutes, resolveMatchedLazies } from "./router";
import "./styles/globals.css";

/** "/euv/" and "/euv" are the same page; the root "/" stays itself. */
function normalizePath(p: string): string {
  return p.length > 1 && p.endsWith("/") ? p.replace(/\/+$/, "") : p;
}

/**
 * Async boot: with route-level code-splitting, the CURRENT location's lazy
 * route(s) must be resolved BEFORE the router is created — otherwise the
 * first render paints createBrowserRouter's null fallback (a blank flash on
 * fresh loads, a guaranteed mismatch when hydrating). The matched page chunk
 * is modulepreload-ed from the prerendered HTML, so this await is a module-
 * cache hit on SSG'd pages, not a network round-trip.
 */
async function bootstrap() {
  const container = document.getElementById("root");
  if (!container) {
    throw new Error("#root element missing from index.html");
  }

  const routes = buildRoutes(isOnBlogSubdomain());
  await resolveMatchedLazies(routes, window.location.pathname);

  // Prerendered pages embed __SSG_PATH__ (the path their markup was rendered
  // for) and __RQ_STATE__ (the dehydrated query cache that markup was
  // rendered FROM). Hydrate only when the embedded path is the page we're
  // actually on AND the root has server markup; anything else — the
  // head-only shells (/_shell, /404.html), /preview/*, a new slug served the
  // generic shell before the next rebuild, a stale cache mismatch —
  // self-heals with a fresh client render, exactly like today's SPA boot.
  const w = window as {
    __SSG_PATH__?: unknown;
    __RQ_STATE__?: unknown;
  };
  const ssgPath = typeof w.__SSG_PATH__ === "string" ? w.__SSG_PATH__ : null;
  const canHydrate =
    ssgPath !== null &&
    normalizePath(window.location.pathname) === normalizePath(ssgPath) &&
    container.firstElementChild !== null;

  const queryClient = createQueryClient();

  if (canHydrate && w.__RQ_STATE__) {
    // Seed the client cache with the exact data the HTML was baked from, so
    // the hydration render paints byte-identical markup instead of pending
    // states.
    hydrate(queryClient, w.__RQ_STATE__);
  }

  const app = (
    <StrictMode>
      <App queryClient={queryClient}>
        <RouterProvider router={createAppRouter(routes)} />
      </App>
    </StrictMode>
  );

  if (canHydrate) {
    // Initial hydration happens synchronously inside hydrateRoot, while the
    // embedded build clock (__SSG_NOW__) is still pinned — clock-derived
    // render output reproduces the baked bytes. Released immediately after,
    // so every later render reads real visit time.
    hydrateRoot(container, app);
    releaseBuildClock();
  } else {
    // Fresh client render: drop any embedded build clock FIRST — this render
    // isn't reproducing baked markup, so it must use the real clock.
    releaseBuildClock();
    createRoot(container).render(app);
  }

  // Warm the other page chunks in the idle background so client-side
  // navigations resolve from the module cache with no fetch stall.
  idlePrefetchRoutes(routes);
}

void bootstrap();
